import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs } from '@carboledger/ui';
import { ROUTES } from '@/app/routes';
import { useTranslations } from '@/hooks';
import {
  buildInventoryFilters,
  PENDING_ORDER_FILTER_KEYS,
  OUTGOING_SHIPMENT_FILTER_KEYS,
  FULFILLMENT_FILTER_KEYS,
} from '@/lib/mappers';
import { EMPTY_MAP } from '@/lib/utils';
import { OrdersTable } from '@/slices/orders';
import { OutgoingShipmentsTable } from '@/slices/outgoing-shipments';
import { AllocationsTable } from '@/slices/allocations';
import { INVENTORY_OUTBOUND_TABS } from '../constants/inventory.constants';
import {
  buildOrderTiles,
  buildOutgoingShipmentTiles,
  buildAllocationTiles,
} from '../constants/inventoryTiles';
import {
  useGetOrdersStripQuery,
  useGetOutgoingShipmentsStripQuery,
  useGetAllocationsStripQuery,
} from '../api/inventoryApi';
import { InventoryListTab } from './InventoryListTab';
import { buildTabEmptyState, buildTabNoResults } from '../constants/inventoryEmptyStates';

/**
 * Props:
 *   module           the active module route segment
 *   certificateType  the module's `CertificateType`
 *   item         the inventory row's facility, material and terminal
 */
export const InventoryOutboundsTabView = ({ module, certificateType, item }) => {
  const t = useTranslations();
  const navigate = useNavigate();
  const [activeSubTab, setActiveSubTab] = useState(INVENTORY_OUTBOUND_TABS.orders);

  const subTabs = useMemo(
    () => [
      { value: INVENTORY_OUTBOUND_TABS.orders, label: t('NAV_ORDERS') },
      { value: INVENTORY_OUTBOUND_TABS.outgoingShipments, label: t('NAV_OUTGOING_SHIPMENTS') },
      { value: INVENTORY_OUTBOUND_TABS.allocations, label: t('NAV_ALLOCATIONS') },
    ],
    [t],
  );

  const header = (
    <Tabs
      tabs={subTabs}
      value={activeSubTab}
      onChange={setActiveSubTab}
      aria-label={t('NAV_OUTBOUNDS')}
    />
  );

  const orderFilters = useMemo(
    () => buildInventoryFilters(item, PENDING_ORDER_FILTER_KEYS),
    [item],
  );
  const shipmentFilters = useMemo(
    () => buildInventoryFilters(item, OUTGOING_SHIPMENT_FILTER_KEYS),
    [item],
  );
  const allocationFilters = useMemo(
    () => buildInventoryFilters(item, FULFILLMENT_FILTER_KEYS),
    [item],
  );

  const certificateArgs = useMemo(() => ({ certificateType }), [certificateType]);

  const addOrder = useCallback(() => navigate(ROUTES.orderCreate(module)), [navigate, module]);
  const addShipment = useCallback(
    () => navigate(ROUTES.outgoingShipmentCreate(module)),
    [navigate, module],
  );

  const ordersEmpty = useMemo(
    () =>
      buildTabEmptyState(t, {
        titleKey: 'ORDERS_EMPTY_TITLE',
        descriptionKey: 'ORDERS_EMPTY_DESCRIPTION',
        addKey: 'ORDERS_ADD',
        onAdd: addOrder,
      }),
    [t, addOrder],
  );
  const shipmentsEmpty = useMemo(
    () =>
      buildTabEmptyState(t, {
        titleKey: 'OUTGOING_SHIPMENTS_EMPTY_TITLE',
        descriptionKey: 'OUTGOING_SHIPMENTS_EMPTY_DESCRIPTION',
        addKey: 'OUTGOING_SHIPMENTS_ADD',
        onAdd: addShipment,
      }),
    [t, addShipment],
  );
  // An allocation is produced by fulfilling a shipment, so this tab offers no Add.
  const allocationsEmpty = useMemo(
    () =>
      buildTabEmptyState(t, {
        titleKey: 'ORDERS_DETAIL_ALLOCATIONS_EMPTY_TITLE',
        descriptionKey: 'ORDERS_DETAIL_ALLOCATIONS_EMPTY_DESCRIPTION',
      }),
    [t],
  );

  const ordersNoResults = useMemo(() => buildTabNoResults(t, 'ORDERS_NO_RESULTS_TITLE'), [t]);
  const shipmentsNoResults = useMemo(
    () => buildTabNoResults(t, 'OUTGOING_SHIPMENTS_NO_RESULTS_TITLE'),
    [t],
  );
  const allocationsNoResults = useMemo(
    () => buildTabNoResults(t, 'ALLOCATIONS_NO_RESULTS_TITLE'),
    [t],
  );

  const openOrder = useCallback(
    (row) => navigate(ROUTES.orderDetail(module, row.id)),
    [navigate, module],
  );
  const openShipment = useCallback(
    (row) => navigate(ROUTES.outgoingShipmentDetail(module, row.id)),
    [navigate, module],
  );
  const openAllocation = useCallback(
    (row) => navigate(ROUTES.allocationDetail(module, row.id)),
    [navigate, module],
  );

  if (activeSubTab === INVENTORY_OUTBOUND_TABS.orders) {
    return (
      <InventoryListTab
        key={activeSubTab}
        header={header}
        filters={orderFilters}
        useStrip={useGetOrdersStripQuery}
        stripArgs={EMPTY_MAP}
        buildTiles={buildOrderTiles}
      >
        {(onEmptyChange) => (
          <OrdersTable
            certificateType={certificateType}
            extraFilters={orderFilters}
            emptyState={ordersEmpty}
            noResults={ordersNoResults}
            onEmptyChange={onEmptyChange}
            onOpenOrder={openOrder}
          />
        )}
      </InventoryListTab>
    );
  }

  if (activeSubTab === INVENTORY_OUTBOUND_TABS.outgoingShipments) {
    return (
      <InventoryListTab
        key={activeSubTab}
        header={header}
        filters={shipmentFilters}
        useStrip={useGetOutgoingShipmentsStripQuery}
        stripArgs={certificateArgs}
        buildTiles={buildOutgoingShipmentTiles}
      >
        {(onEmptyChange) => (
          <OutgoingShipmentsTable
            certificateType={certificateType}
            extraFilters={shipmentFilters}
            emptyState={shipmentsEmpty}
            noResults={shipmentsNoResults}
            onEmptyChange={onEmptyChange}
            onOpenShipment={openShipment}
          />
        )}
      </InventoryListTab>
    );
  }

  return (
    <InventoryListTab
      key={activeSubTab}
      header={header}
      filters={allocationFilters}
      useStrip={useGetAllocationsStripQuery}
      stripArgs={certificateArgs}
      buildTiles={buildAllocationTiles}
    >
      {(onEmptyChange) => (
        <AllocationsTable
          certificateType={certificateType}
          extraFilters={allocationFilters}
          emptyState={allocationsEmpty}
          noResults={allocationsNoResults}
          onEmptyChange={onEmptyChange}
          onOpen={openAllocation}
        />
      )}
    </InventoryListTab>
  );
};
